import { Option, Value } from 'baseui/select';
import Separator from 'components/common/separator';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import Text from 'components/elements/text';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { addNewAddOnsItem } from '../../hooks/use-get-default-values';
import {
  TicketSaleFlightAddOnsFormType,
  TicketSaleFormType,
} from '../form-type';

interface Props {
  parentIndex: number;
}

function useScheduleAddOnsTableList(parentIndex: number) {
  const { t } = useTranslation();
  const { editable } = useFormState();

  const name = `flightSales.${parentIndex}.flightSaleGuests` as const;

  const { control } = useFormContext<TicketSaleFormType>();

  const guests = useWatch({
    control,
    name,
  });

  const guestList = React.useMemo(() => {
    const options = guests
      .filter((guest) => !!guest.guestId)
      .reduce((prev, guest) => {
        const isIncluded = prev.find((item) => item.id === guest.guestId);

        if (isIncluded) return prev;

        prev.push({
          id: guest.guestId,
          label: guest.guestName,
          extra: guest,
        });

        return prev;
      }, [] as Option[]);

    return options;
  }, [guests]);

  const columns: IColumn<TicketSaleFlightAddOnsFormType>[] =
    React.useMemo(() => {
      return [
        {
          header: t('common:name'),
          accessorKey: 'guestId',
          minSize: 100,
          dataType: ColumnDataType.select,
          options: guestList,
          required: true,
        },
        {
          header: t('common:route'),
          accessorKey: 'route',
          minSize: 100,
          dataType: ColumnDataType.text,
          required: true,
        },
        {
          header: t('common:description'),
          accessorKey: 'description',
          minSize: 500,
          dataType: ColumnDataType.text,
        },
        {
          header: t('common:price'),
          accessorKey: 'price',
          minSize: 100,
          dataType: ColumnDataType.number,
          minNumber: 0,
          required: true,
        },
        ...(editable
          ? [
              {
                header: '',
                accessorKey: 'delete',
                minSize: 60,
                size: 60,
                maxSize: 60,
                dataType: ColumnDataType.delete,
              },
            ]
          : []),
      ];
    }, [editable, guestList, t]);

  return columns;
}

export default function ScheduleAddOnsTable(props: Props) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const name = `flightSales.${props.parentIndex}.flightSaleAddOns` as const;
  const { clearErrors, getValues, control, trigger } =
    useFormContext<TicketSaleFormType>();

  const { fields, update, remove, append } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const columns = useScheduleAddOnsTableList(props.parentIndex);

  const dispatch = React.useCallback(
    (action) => {
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof TicketSaleFlightAddOnsFormType;
      const currentRowIndex = action.rowIndex as number;
      const addOns = getValues(name);
      const addOn = addOns[currentRowIndex];
      const value = action.value;
      const extra = action.extra as Value;

      const otherVal: () => Partial<TicketSaleFlightAddOnsFormType> = () => {
        if (columnId === 'guestId') {
          return {
            guestName: extra?.[0]?.label as string,
          };
        }
        return {};
      };

      if (type === 'addRow') {
        append(addNewAddOnsItem());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...addOn,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [append, clearErrors, getValues, name, remove, trigger, update],
  );

  if (!!fields.length || editable) {
    return (
      <>
        <Text variant="ParagraphMedium">{t('ticketSales:add_ons')}</Text>
        <Separator gap={12} />
        <div style={{ width: '100%' }}>
          <TableInput
            columns={columns}
            data={fields}
            dispatch={dispatch}
            disabled={!editable}
            name={name}
            uniqueRowKey="customId"
          />
        </div>
      </>
    );
  }
  return null;
}
